// TODO - Type 'any' needs to be fixed.
// import Button from "@gigandtake/button";

import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import {
	FormControl,
	InputAdornment,
	MenuItem,
	SelectChangeEvent, Skeleton,
	TextField
} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React, { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import {
	ATTRIBUTES,
	HOURS,
	LABOR_REQUIREMENTS,
	PRODUCTION_PLAN,
	ROSTER,
	SETTINGS_TABS,
	SHIFT_ROSTER,
	SHIFTS, // eslint-disable-line sort-imports
	WORKERS,
} from "../constants";
import { planner } from "../planner-ui";
import { confirmLeaveWithDrafts } from "../roster/load-balance-board/utils/pending-drafts";

interface ISchedulerHeaderProps {
	activeTab: string;
	setActiveTab: Dispatch<SetStateAction<string>>;
	selectedLocation: number;
	handleLocationChange: (event: SelectChangeEvent<{ value: string }>) => void;
	allLocations: { id: number; name: string }[];
	ftSchedulerEnabledLocations: { id: number; name: string }[];
}

// Tab label: uppercase Inter with the design-system caps tracking. Size stays
// at 14px — brick underline (not colour) carries the active state.
const tabLabelSx = (isActive: boolean) => ({
	fontFamily: planner.fontUi,
	color: isActive ? planner.navy : planner.inkSecondary,
	fontSize: "14px",
	fontWeight: isActive ? 700 : 500,
	lineHeight: "24px",
	letterSpacing: planner.labelCaps.letterSpacing,
	textTransform: "uppercase" as const,
	whiteSpace: "nowrap" as const,
});

// Inactive tabs keep a transparent underline of the same weight so the row
// does not shift by 2px when the selection moves.
const tabBoxSx = (isActive: boolean) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	padding: "8px 0px 8px 0px",
	borderBottom: `2px solid ${isActive ? planner.accent : "transparent"}`,
	cursor: "pointer",
	flexShrink: 0,
});

// Standard-variant select, restyled to the design system: Inter for the label
// and value, hairline rest state, brick on focus.
const locationSelectSx = {
	"& .MuiInputLabel-root": {
		fontFamily: planner.fontUi,
		color: planner.inkSecondary,
		"&.Mui-focused": { color: planner.accent },
	},
	"& .MuiInput-root": {
		fontFamily: planner.fontUi,
		fontSize: 14,
		color: planner.ink,
	},
	"& .MuiInput-root:before": { borderBottomColor: planner.hairlineStrong },
	"& .MuiInput-root:hover:not(.Mui-disabled, .Mui-error):before": {
		borderBottomColor: planner.inkMuted,
	},
	"& .MuiInput-root:after": { borderBottomColor: planner.accent },
};

const FullTimeSchedulerHeader: React.FC<ISchedulerHeaderProps> = ({
	activeTab,
	setActiveTab,
	selectedLocation,
	handleLocationChange,
	allLocations,
	ftSchedulerEnabledLocations,
}) => {
	const { t } = useTranslation();
	const inSettings = SETTINGS_TABS.includes(activeTab);
	// Leaving the roster with drafted placements asks first; a yes drops them.
	const switchTab = (tab: string) => {
		if ((activeTab === ROSTER || activeTab === SHIFT_ROSTER) && tab !== activeTab && !confirmLeaveWithDrafts()) {
			return;
		}
		setActiveTab(tab);
	};
	const getValidSelectedLocation = (id: number): number | "" => {
		// While locations are still loading, render an empty (controlled) value —
		// never null, which MUI Select rejects.
		if (!ftSchedulerEnabledLocations || ftSchedulerEnabledLocations.length === 0) {
			return "";
		}
		const location = ftSchedulerEnabledLocations.find(
			(loc: { id: number }) => loc.id === id
		);
		// Fall back to the first FT-enabled location when the current selection is
		// invalid/absent; the parent effect dispatches the matching store update.
		return location ? location.id : ftSchedulerEnabledLocations[0].id;
	};
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "flex-start",
				justifyContent: "center",
				background: planner.surface,
				fontFamily: planner.fontUi,
				padding: { xs: "12px 12px 0px 12px", md: "28px 24px 0px 24px" },
				borderRadius: `${planner.radius}px ${planner.radius}px 0px 0px`,
			}}
		>
			<Box
				sx={{
					display: "flex",
					// Phones: location picker on its own line above a tab row that scrolls sideways.
					flexDirection: { xs: "column-reverse", md: "row" },
					justifyContent: "space-between",
					alignItems: { xs: "stretch", md: "center" },
					textAlign: "center",
					height: { xs: "auto", md: "41px" },
					gap: { xs: 1, md: 0 },
					width: "100%",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						height: { xs: "41px", md: "100%" },
						alignItems: "flex-end",
						justifyContent: "flex-start",
						overflowX: { xs: "auto", md: "visible" },
						overflowY: "hidden",
						scrollbarWidth: "none",
						"&::-webkit-scrollbar": { display: "none" },
					}}
				>
					{/* The crew schedule (Shifts, Workers) comes first; the planner tabs
					    follow. Which one a company lands on is decided in SchedulerTabs. */}
					<Box
						sx={tabBoxSx(activeTab === SHIFT_ROSTER)}
						onClick={() => {
							switchTab(SHIFT_ROSTER);
						}}
					>
						<Typography sx={tabLabelSx(activeTab === SHIFT_ROSTER)}>
							Shift Roster
						</Typography>
					</Box>
					<Box
						sx={{ ...tabBoxSx(activeTab === PRODUCTION_PLAN), marginLeft: "24px" }}
						onClick={() => {
							switchTab(PRODUCTION_PLAN);
						}}
					>
						<Typography sx={tabLabelSx(activeTab === PRODUCTION_PLAN)}>
							Work Plan
						</Typography>
					</Box>
					<Box
						sx={{ ...tabBoxSx(inSettings), marginLeft: "24px" }}
						onClick={() => {
							if (!inSettings) switchTab(LABOR_REQUIREMENTS);
						}}
					>
						{/* Standards, Hours and Setup live under Settings, as sub-tabs. */}
						<Typography sx={tabLabelSx(inSettings)}>
							Settings
						</Typography>
					</Box>
				</Box>
				<Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
					<Box sx={{ width: { xs: "100%", md: 200 } }}>
						<FormControl fullWidth variant="standard">
							<TextField
								select
								// No visible label: the pin adornment already says "location", so the
								// floating label only repeated it. Kept for screen readers.
								inputProps={{ "aria-label": "Location" }}
								value={getValidSelectedLocation(selectedLocation)}
								onChange={handleLocationChange}
								variant="standard"
								sx={locationSelectSx}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<LocationOnOutlinedIcon sx={{ color: planner.inkMuted }} />
										</InputAdornment>
									),
								}}
							>
								{ftSchedulerEnabledLocations && ftSchedulerEnabledLocations.length > 0 ? (
									ftSchedulerEnabledLocations.map((location: { id: number; name: string }) => (
										<MenuItem key={location.id} value={location.id} sx={{ fontFamily: planner.fontUi, fontSize: 14 }}>
											{location.name}
										</MenuItem>
									))
								) : (
									<MenuItem value="">
										<Skeleton variant="text" width={120} />
									</MenuItem>
								)}
							</TextField>
						</FormControl>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default FullTimeSchedulerHeader;
